/**
 * Extraordinary Meeting Wizard
 *
 * A 3-step wizard for creating extraordinary meeting protocols (extra bolagsstämma)
 * according to ABL 7 kap 13-14§.
 *
 * Features:
 * - Support for different meeting reasons (capital change, bylaw amendment, board change, minority request)
 * - Shareholder attendance tracking with share/vote counts
 * - Flexible decision sections based on meeting reason
 *
 * Steps:
 * 1. Meeting Details - Date, time, place, attendees (shareholders)
 * 2. Reason & Decisions - Meeting reason and related decisions
 * 3. Review & Save
 */

import { useState, useEffect, useCallback } from "react";
import {
  Calendar,
  Clock,
  MapPin,
  Users,
  FileText,
  ChevronRight,
  ChevronLeft,
  Check,
  Loader2,
  AlertCircle,
  Plus,
  Trash2,
  Scale,
  Building2,
  UserPlus,
  HelpCircle,
} from "lucide-react";
import { toast } from "sonner";
import { Modal } from "@/components/common/Modal";
import {
  protocolsApi,
  type ExtraordinaryMeetingPrefillData,
  type ExtraordinaryMeetingAttendee,
  type ExtraordinaryMeetingDecisions,
  type CreateExtraordinaryMeetingData,
  type ExtraordinaryReasonType,
} from "@/lib/api";

interface ExtraordinaryMeetingWizardProps {
  isOpen: boolean;
  onClose: () => void;
  companyId: string;
  onSuccess?: (protocolId: string) => void;
}

type WizardStep = 1 | 2 | 3;

interface MeetingDetails {
  meetingDate: string;
  meetingTime: string;
  meetingPlace: string;
  chairpersonName: string;
  secretaryName: string;
  adjusterName: string;
}

const REASON_LABELS: Record<ExtraordinaryReasonType, string> = {
  capital_change: "Kapitaländring",
  bylaw_amendment: "Bolagsordningsändring",
  board_change: "Styrelseändring",
  minority_request: "Begäran från aktieägare (ABL 7:13)",
  other: "Övrigt",
};

const REASON_DESCRIPTIONS: Record<ExtraordinaryReasonType, string> = {
  capital_change: "Nyemission, minskning av aktiekapital eller annan kapitaländring",
  bylaw_amendment: "Ändring av bolagsordningen",
  board_change: "Val av nya styrelseledamöter eller entledigande",
  minority_request: "Stämma begärd av aktieägare med minst 10% av aktierna",
  other: "Annat ärende som kräver bolagsstämmobeslut",
};

const REASON_ICONS: Record<ExtraordinaryReasonType, React.ReactNode> = {
  capital_change: <Scale className="w-5 h-5" />,
  bylaw_amendment: <FileText className="w-5 h-5" />,
  board_change: <UserPlus className="w-5 h-5" />,
  minority_request: <Users className="w-5 h-5" />,
  other: <HelpCircle className="w-5 h-5" />,
};

export function ExtraordinaryMeetingWizard({
  isOpen,
  onClose,
  companyId,
  onSuccess,
}: ExtraordinaryMeetingWizardProps) {
  const [step, setStep] = useState<WizardStep>(1);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [prefillData, setPrefillData] = useState<ExtraordinaryMeetingPrefillData | null>(null);

  // Step 1: Meeting Details
  const [meetingDetails, setMeetingDetails] = useState<MeetingDetails>({
    meetingDate: new Date().toISOString().split("T")[0],
    meetingTime: "",
    meetingPlace: "",
    chairpersonName: "",
    secretaryName: "",
    adjusterName: "",
  });

  // Shareholder attendees
  const [attendees, setAttendees] = useState<ExtraordinaryMeetingAttendee[]>([]);

  // Step 2: Decisions
  const [decisions, setDecisions] = useState<ExtraordinaryMeetingDecisions>({
    reason: "other",
    otherDecisions: [],
  });

  // Load prefill data
  useEffect(() => {
    if (!isOpen) return;

    const loadPrefillData = async () => {
      setIsLoading(true);
      try {
        const response = await protocolsApi.getExtraordinaryMeetingPrefill(companyId);
        if (response.success && response.data) {
          setPrefillData(response.data);

          // Pre-fill meeting place
          setMeetingDetails((prev) => ({
            ...prev,
            meetingPlace: response.data!.company.seatMunicipality || "",
          }));

          // Pre-fill attendees from shareholders
          const shareholderAttendees: ExtraordinaryMeetingAttendee[] = response.data.shareholders.map(
            (s) => ({
              name: s.name,
              shares: s.totalShares,
              votes: s.totalVotes,
              present: true,
            })
          );
          setAttendees(shareholderAttendees);
        }
      } catch (error) {
        console.error("Failed to load prefill data:", error);
        toast.error("Kunde inte ladda data");
      } finally {
        setIsLoading(false);
      }
    };

    loadPrefillData();
  }, [isOpen, companyId]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setStep(1);
      setMeetingDetails({
        meetingDate: new Date().toISOString().split("T")[0],
        meetingTime: "",
        meetingPlace: "",
        chairpersonName: "",
        secretaryName: "",
        adjusterName: "",
      });
      setAttendees([]);
      setDecisions({
        reason: "other",
        otherDecisions: [],
      });
    }
  }, [isOpen]);

  const validateStep1 = useCallback((): boolean => {
    if (!meetingDetails.meetingDate) {
      toast.error("Ange mötesdatum");
      return false;
    }
    if (!meetingDetails.chairpersonName) {
      toast.error("Ange ordförandens namn");
      return false;
    }
    const presentAttendees = attendees.filter((a) => a.present);
    if (presentAttendees.length === 0) {
      toast.error("Minst en aktieägare måste vara närvarande");
      return false;
    }
    return true;
  }, [meetingDetails, attendees]);

  const validateStep2 = useCallback((): boolean => {
    if (!decisions.reason) {
      toast.error("Välj anledning för extra bolagsstämma");
      return false;
    }

    // Validate based on reason
    if (decisions.reason === "capital_change") {
      if (!decisions.capitalChange?.amount || decisions.capitalChange.amount <= 0) {
        toast.error("Ange belopp för kapitaländring");
        return false;
      }
    }

    if (decisions.reason === "bylaw_amendment") {
      if (!decisions.bylawAmendment?.description) {
        toast.error("Beskriv bolagsordningsändringen");
        return false;
      }
    }

    if (decisions.reason === "board_change") {
      if (!decisions.boardElection?.members || decisions.boardElection.members.length === 0) {
        toast.error("Lägg till minst en styrelseledamot");
        return false;
      }
    }

    return true;
  }, [decisions]);

  const handleNext = () => {
    if (step === 1 && !validateStep1()) return;
    if (step === 2 && !validateStep2()) return;
    if (step < 3) setStep((step + 1) as WizardStep);
  };

  const handleBack = () => {
    if (step > 1) setStep((step - 1) as WizardStep);
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const data: CreateExtraordinaryMeetingData = {
        meetingDate: meetingDetails.meetingDate,
        meetingTime: meetingDetails.meetingTime || undefined,
        meetingPlace: meetingDetails.meetingPlace || undefined,
        chairpersonName: meetingDetails.chairpersonName,
        secretaryName: meetingDetails.secretaryName || undefined,
        adjusterName: meetingDetails.adjusterName || undefined,
        attendees: attendees,
        decisions: decisions,
      };

      const response = await protocolsApi.createExtraordinaryMeeting(companyId, data);

      if (response.success && response.data) {
        // Toast is shown in ProtocolList.handleExtraordinaryMeetingSuccess
        onSuccess?.(response.data.protocol.id);
        onClose();
      } else {
        toast.error(response.error?.message || "Kunde inte skapa protokoll");
      }
    } catch (error) {
      console.error("Failed to create protocol:", error);
      toast.error("Ett fel uppstod");
    } finally {
      setIsSaving(false);
    }
  };

  const stepTitles: Record<WizardStep, string> = {
    1: "Mötesuppgifter",
    2: "Ärende",
    3: "Granska",
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={
        <div className="flex items-center gap-3">
          <Building2 className="w-5 h-5 text-primary-600" />
          <span>Extra bolagsstämma</span>
        </div>
      }
      size="2xl"
    >
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
        </div>
      ) : (
        <div className="space-y-6">
          {/* Progress Steps */}
          <div className="flex items-center justify-between">
            {[1, 2, 3].map((s) => (
              <div key={s} className="flex items-center">
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors ${
                    s === step
                      ? "bg-primary-600 text-white"
                      : s < step
                        ? "bg-green-500 text-white"
                        : "bg-gray-200 text-gray-600"
                  }`}
                >
                  {s < step ? <Check className="w-4 h-4" /> : s}
                </div>
                <span
                  className={`ml-2 text-sm font-medium ${
                    s === step ? "text-gray-900" : "text-gray-500"
                  }`}
                >
                  {stepTitles[s as WizardStep]}
                </span>
                {s < 3 && (
                  <ChevronRight
                    className={`w-5 h-5 mx-4 ${s < step ? "text-green-500" : "text-gray-300"}`}
                  />
                )}
              </div>
            ))}
          </div>

          {/* Company Info */}
          {prefillData && (
            <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
              <p className="text-sm font-medium text-gray-900">{prefillData.company.name}</p>
              <p className="text-xs text-gray-500">Org.nr: {prefillData.company.organizationNumber}</p>
            </div>
          )}

          {/* Step Content */}
          <div className="min-h-[400px]">
            {step === 1 && (
              <Step1MeetingDetails
                details={meetingDetails}
                onChange={setMeetingDetails}
                attendees={attendees}
                onAttendeesChange={setAttendees}
                prefillData={prefillData}
              />
            )}
            {step === 2 && (
              <Step2ReasonDecisions
                decisions={decisions}
                onChange={setDecisions}
                prefillData={prefillData}
              />
            )}
            {step === 3 && (
              <Step3Review
                meetingDetails={meetingDetails}
                attendees={attendees}
                decisions={decisions}
                prefillData={prefillData}
              />
            )}
          </div>

          {/* Navigation Buttons */}
          <div className="flex justify-between pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={step === 1 ? onClose : handleBack}
              className="btn btn-secondary"
              disabled={isSaving}
            >
              {step === 1 ? (
                "Avbryt"
              ) : (
                <>
                  <ChevronLeft className="w-4 h-4 mr-1" />
                  Tillbaka
                </>
              )}
            </button>

            {step < 3 ? (
              <button type="button" onClick={handleNext} className="btn btn-primary">
                Fortsätt
                <ChevronRight className="w-4 h-4 ml-1" />
              </button>
            ) : (
              <button
                type="button"
                onClick={handleSave}
                disabled={isSaving}
                className="btn btn-primary flex items-center gap-2"
              >
                {isSaving ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Sparar...
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4" />
                    Skapa protokoll
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      )}
    </Modal>
  );
}

// ═══════════════════════════════════════════════════════════════════
// Step 1: Meeting Details
// ═══════════════════════════════════════════════════════════════════

interface Step1Props {
  details: MeetingDetails;
  onChange: (details: MeetingDetails) => void;
  attendees: ExtraordinaryMeetingAttendee[];
  onAttendeesChange: (attendees: ExtraordinaryMeetingAttendee[]) => void;
  prefillData: ExtraordinaryMeetingPrefillData | null;
}

function Step1MeetingDetails({
  details,
  onChange,
  attendees,
  onAttendeesChange,
  prefillData: _prefillData,
}: Step1Props) {
  const updateField = (field: keyof MeetingDetails, value: string) => {
    onChange({ ...details, [field]: value });
  };

  const toggleAttendeePresent = (index: number) => {
    const updated = [...attendees];
    updated[index] = { ...updated[index], present: !updated[index].present };
    onAttendeesChange(updated);
  };

  const updateAttendeeRepresentedBy = (index: number, value: string) => {
    const updated = [...attendees];
    updated[index] = { ...updated[index], representedBy: value || undefined };
    onAttendeesChange(updated);
  };

  const totalShares = attendees.reduce((sum, a) => sum + a.shares, 0);
  const presentShares = attendees.filter((a) => a.present).reduce((sum, a) => sum + a.shares, 0);

  return (
    <div className="space-y-5">
      <p className="text-sm text-gray-600">
        Ange mötesuppgifter för den extra bolagsstämman enligt ABL 7 kap 13-14§.
      </p>

      {/* Date, Time, Place */}
      <div className="grid grid-cols-3 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            <Calendar className="w-4 h-4 inline mr-1" />
            Datum *
          </label>
          <input
            type="date"
            value={details.meetingDate}
            onChange={(e) => updateField("meetingDate", e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            <Clock className="w-4 h-4 inline mr-1" />
            Tid
          </label>
          <input
            type="time"
            value={details.meetingTime}
            onChange={(e) => updateField("meetingTime", e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            <MapPin className="w-4 h-4 inline mr-1" />
            Plats
          </label>
          <input
            type="text"
            value={details.meetingPlace}
            onChange={(e) => updateField("meetingPlace", e.target.value)}
            placeholder="T.ex. Bolagets kontor"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>
      </div>

      {/* Officials */}
      <div className="grid grid-cols-3 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Ordförande *
          </label>
          <input
            type="text"
            value={details.chairpersonName}
            onChange={(e) => updateField("chairpersonName", e.target.value)}
            placeholder="Namn"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Protokollförare
          </label>
          <input
            type="text"
            value={details.secretaryName}
            onChange={(e) => updateField("secretaryName", e.target.value)}
            placeholder="Namn (valfritt)"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Justerare
          </label>
          <input
            type="text"
            value={details.adjusterName}
            onChange={(e) => updateField("adjusterName", e.target.value)}
            placeholder="Namn (valfritt)"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>
      </div>

      {/* Shareholders */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
            <Users className="w-4 h-4" />
            Aktieägare
          </h4>
          <div className="text-xs text-gray-500">
            Närvarande: {presentShares.toLocaleString("sv-SE")} av {totalShares.toLocaleString("sv-SE")} aktier
            ({totalShares > 0 ? Math.round((presentShares / totalShares) * 100) : 0}%)
          </div>
        </div>

        {attendees.length === 0 ? (
          <div className="text-center py-6 text-gray-500 bg-gray-50 rounded-lg">
            <Users className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p className="text-sm">Inga aktieägare hittades</p>
          </div>
        ) : (
          <div className="space-y-2">
            {attendees.map((attendee, index) => (
              <div
                key={index}
                className={`p-3 rounded-lg border transition-colors ${
                  attendee.present
                    ? "bg-green-50 border-green-200"
                    : "bg-gray-50 border-gray-200"
                }`}
              >
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={attendee.present}
                    onChange={() => toggleAttendeePresent(index)}
                    className="w-4 h-4 text-primary-600 rounded border-gray-300 focus:ring-primary-500"
                  />
                  <div className="flex-1">
                    <p className="font-medium text-gray-900">{attendee.name}</p>
                    <p className="text-xs text-gray-500">
                      {attendee.shares.toLocaleString("sv-SE")} aktier,{" "}
                      {attendee.votes.toLocaleString("sv-SE")} röster
                    </p>
                  </div>
                  {attendee.present && <Check className="w-4 h-4 text-green-600" />}
                </label>
                {attendee.present && (
                  <div className="mt-2 ml-7">
                    <input
                      type="text"
                      value={attendee.representedBy || ""}
                      onChange={(e) => updateAttendeeRepresentedBy(index, e.target.value)}
                      placeholder="Genom ombud (lämna tomt om personligt närvarande)"
                      className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {presentShares > 0 && totalShares > 0 && presentShares >= totalShares / 2 && (
          <div className="mt-3 p-2 bg-green-50 rounded border border-green-200">
            <p className="text-sm text-green-800">
              <Check className="w-4 h-4 inline mr-1" />
              Stämman är beslutför ({Math.round((presentShares / totalShares) * 100)}% närvarande)
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// Step 2: Reason & Decisions
// ═══════════════════════════════════════════════════════════════════

interface Step2Props {
  decisions: ExtraordinaryMeetingDecisions;
  onChange: (decisions: ExtraordinaryMeetingDecisions) => void;
  prefillData: ExtraordinaryMeetingPrefillData | null;
}

function Step2ReasonDecisions({ decisions, onChange, prefillData: _prefillData }: Step2Props) {
  const setReason = (reason: ExtraordinaryReasonType) => {
    // Reset related fields when changing reason
    onChange({
      ...decisions,
      reason,
      reasonDescription: undefined,
      capitalChange: reason === "capital_change" ? { type: "increase", amount: 0 } : undefined,
      bylawAmendment: reason === "bylaw_amendment" ? { sections: [], description: "" } : undefined,
      boardElection:
        reason === "board_change" ? { performed: true, members: [] } : undefined,
      otherDecisions: reason === "other" ? [] : decisions.otherDecisions,
    });
  };

  const addBoardMember = () => {
    const members = decisions.boardElection?.members || [];
    onChange({
      ...decisions,
      boardElection: {
        performed: true,
        members: [...members, { name: "", role: "Styrelseledamot" }],
      },
    });
  };

  const updateBoardMember = (index: number, field: "name" | "role", value: string) => {
    const members = [...(decisions.boardElection?.members || [])];
    members[index] = { ...members[index], [field]: value };
    onChange({
      ...decisions,
      boardElection: { performed: true, members },
    });
  };

  const removeBoardMember = (index: number) => {
    const members = (decisions.boardElection?.members || []).filter((_, i) => i !== index);
    onChange({
      ...decisions,
      boardElection: { performed: true, members },
    });
  };

  const addOtherDecision = () => {
    const otherDecisions = decisions.otherDecisions || [];
    onChange({
      ...decisions,
      otherDecisions: [...otherDecisions, { title: "", decision: "" }],
    });
  };

  const updateOtherDecision = (index: number, field: "title" | "decision", value: string) => {
    const otherDecisions = [...(decisions.otherDecisions || [])];
    otherDecisions[index] = { ...otherDecisions[index], [field]: value };
    onChange({ ...decisions, otherDecisions });
  };

  const removeOtherDecision = (index: number) => {
    const otherDecisions = (decisions.otherDecisions || []).filter((_, i) => i !== index);
    onChange({ ...decisions, otherDecisions });
  };

  return (
    <div className="space-y-5">
      <p className="text-sm text-gray-600">
        Välj anledning för den extra bolagsstämman och ange relevanta beslut.
      </p>

      {/* Reason Selection */}
      <div>
        <label className="block text-sm font-semibold text-gray-900 mb-3">
          Anledning till extra bolagsstämma *
        </label>
        <div className="grid grid-cols-1 gap-2">
          {(Object.keys(REASON_LABELS) as ExtraordinaryReasonType[]).map((reason) => (
            <button
              key={reason}
              type="button"
              onClick={() => setReason(reason)}
              className={`p-4 rounded-lg border text-left transition-all flex items-start gap-3 ${
                decisions.reason === reason
                  ? "bg-primary-50 border-primary-500 ring-2 ring-primary-200"
                  : "bg-white border-gray-200 hover:border-gray-300"
              }`}
            >
              <div
                className={`p-2 rounded-lg ${
                  decisions.reason === reason ? "bg-primary-100 text-primary-600" : "bg-gray-100 text-gray-500"
                }`}
              >
                {REASON_ICONS[reason]}
              </div>
              <div className="flex-1">
                <p className="font-medium text-gray-900">{REASON_LABELS[reason]}</p>
                <p className="text-sm text-gray-500">{REASON_DESCRIPTIONS[reason]}</p>
              </div>
              {decisions.reason === reason && (
                <Check className="w-5 h-5 text-primary-600 mt-1" />
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Reason-specific fields */}
      {decisions.reason === "capital_change" && (
        <div className="p-4 bg-gray-50 rounded-lg border border-gray-200 space-y-4">
          <h4 className="font-medium text-gray-900">Kapitaländring</h4>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-gray-700 mb-1">Typ av ändring *</label>
              <select
                value={decisions.capitalChange?.type || "increase"}
                onChange={(e) =>
                  onChange({
                    ...decisions,
                    capitalChange: {
                      ...decisions.capitalChange!,
                      type: e.target.value as "increase" | "decrease",
                    },
                  })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="increase">Ökning (nyemission)</option>
                <option value="decrease">Minskning</option>
              </select>
            </div>
            <div>
              <label className="block text-sm text-gray-700 mb-1">Belopp (kr) *</label>
              <input
                type="number"
                value={decisions.capitalChange?.amount || ""}
                onChange={(e) =>
                  onChange({
                    ...decisions,
                    capitalChange: {
                      ...decisions.capitalChange!,
                      amount: parseInt(e.target.value) || 0,
                    },
                  })
                }
                placeholder="0"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm text-gray-700 mb-1">Metod</label>
            <input
              type="text"
              value={decisions.capitalChange?.method || ""}
              onChange={(e) =>
                onChange({
                  ...decisions,
                  capitalChange: { ...decisions.capitalChange!, method: e.target.value },
                })
              }
              placeholder="T.ex. kontantemission, apportemission, fondemission"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
          <div>
            <label className="block text-sm text-gray-700 mb-1">Beskrivning</label>
            <textarea
              value={decisions.capitalChange?.description || ""}
              onChange={(e) =>
                onChange({
                  ...decisions,
                  capitalChange: { ...decisions.capitalChange!, description: e.target.value },
                })
              }
              placeholder="Ytterligare information om kapitaländringen..."
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
        </div>
      )}

      {decisions.reason === "bylaw_amendment" && (
        <div className="p-4 bg-gray-50 rounded-lg border border-gray-200 space-y-4">
          <h4 className="font-medium text-gray-900">Bolagsordningsändring</h4>
          <div>
            <label className="block text-sm text-gray-700 mb-1">Vilka paragrafer ändras</label>
            <input
              type="text"
              value={decisions.bylawAmendment?.sections.join(", ") || ""}
              onChange={(e) =>
                onChange({
                  ...decisions,
                  bylawAmendment: {
                    ...decisions.bylawAmendment!,
                    sections: e.target.value.split(",").map((s) => s.trim()).filter(Boolean),
                  },
                })
              }
              placeholder="T.ex. § 3, § 5"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
          <div>
            <label className="block text-sm text-gray-700 mb-1">Beskrivning av ändringen *</label>
            <textarea
              value={decisions.bylawAmendment?.description || ""}
              onChange={(e) =>
                onChange({
                  ...decisions,
                  bylawAmendment: { ...decisions.bylawAmendment!, description: e.target.value },
                })
              }
              placeholder="Beskriv ändringarna i bolagsordningen..."
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
        </div>
      )}

      {decisions.reason === "board_change" && (
        <div className="p-4 bg-gray-50 rounded-lg border border-gray-200 space-y-4">
          <div className="flex items-center justify-between">
            <h4 className="font-medium text-gray-900">Styrelseändring</h4>
            <button
              type="button"
              onClick={addBoardMember}
              className="btn btn-sm btn-secondary flex items-center gap-1"
            >
              <Plus className="w-4 h-4" />
              Lägg till
            </button>
          </div>

          {(!decisions.boardElection?.members || decisions.boardElection.members.length === 0) ? (
            <div className="text-center py-4 text-gray-500">
              <UserPlus className="w-6 h-6 mx-auto mb-1 opacity-50" />
              <p className="text-sm">Lägg till styrelseledamöter som väljs</p>
            </div>
          ) : (
            <div className="space-y-2">
              {decisions.boardElection.members.map((member, index) => (
                <div key={index} className="flex items-center gap-2 bg-white p-2 rounded border">
                  <input
                    type="text"
                    value={member.name}
                    onChange={(e) => updateBoardMember(index, "name", e.target.value)}
                    placeholder="Namn"
                    className="flex-1 px-2 py-1 border border-gray-300 rounded focus:ring-1 focus:ring-primary-500"
                  />
                  <select
                    value={member.role}
                    onChange={(e) => updateBoardMember(index, "role", e.target.value)}
                    className="px-2 py-1 border border-gray-300 rounded focus:ring-1 focus:ring-primary-500"
                  >
                    <option value="Styrelseordförande">Styrelseordförande</option>
                    <option value="Styrelseledamot">Styrelseledamot</option>
                    <option value="Styrelsesuppleant">Styrelsesuppleant</option>
                  </select>
                  <button
                    type="button"
                    onClick={() => removeBoardMember(index)}
                    className="p-1 text-gray-400 hover:text-red-500"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {decisions.reason === "minority_request" && (
        <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
          <h4 className="font-medium text-gray-900 mb-2">Begäran från aktieägare</h4>
          <div>
            <label className="block text-sm text-gray-700 mb-1">
              Beskriv ärendet som begärts behandlas
            </label>
            <textarea
              value={decisions.reasonDescription || ""}
              onChange={(e) =>
                onChange({ ...decisions, reasonDescription: e.target.value })
              }
              placeholder="Beskriv det ärende som minoritetsaktieägarna begärt att stämman ska behandla..."
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
        </div>
      )}

      {decisions.reason === "other" && (
        <div className="p-4 bg-gray-50 rounded-lg border border-gray-200 space-y-4">
          <div className="flex items-center justify-between">
            <h4 className="font-medium text-gray-900">Övriga ärenden</h4>
            <button
              type="button"
              onClick={addOtherDecision}
              className="btn btn-sm btn-secondary flex items-center gap-1"
            >
              <Plus className="w-4 h-4" />
              Lägg till beslut
            </button>
          </div>

          <div>
            <label className="block text-sm text-gray-700 mb-1">Anledning till stämman</label>
            <textarea
              value={decisions.reasonDescription || ""}
              onChange={(e) =>
                onChange({ ...decisions, reasonDescription: e.target.value })
              }
              placeholder="Beskriv kort anledningen till att extra bolagsstämma hålls..."
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>

          {(!decisions.otherDecisions || decisions.otherDecisions.length === 0) ? (
            <div className="text-center py-4 text-gray-500">
              <FileText className="w-6 h-6 mx-auto mb-1 opacity-50" />
              <p className="text-sm">Lägg till beslut som fattas på stämman</p>
            </div>
          ) : (
            <div className="space-y-3">
              {decisions.otherDecisions.map((item, index) => (
                <div key={index} className="bg-white p-3 rounded border space-y-2">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-primary-600">§ {index + 1}</span>
                    <input
                      type="text"
                      value={item.title}
                      onChange={(e) => updateOtherDecision(index, "title", e.target.value)}
                      placeholder="Rubrik"
                      className="flex-1 px-2 py-1 border border-gray-300 rounded focus:ring-1 focus:ring-primary-500"
                    />
                    <button
                      type="button"
                      onClick={() => removeOtherDecision(index)}
                      className="p-1 text-gray-400 hover:text-red-500"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                  <textarea
                    value={item.decision}
                    onChange={(e) => updateOtherDecision(index, "decision", e.target.value)}
                    placeholder="Beslutstext..."
                    rows={2}
                    className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-primary-500"
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// Step 3: Review
// ═══════════════════════════════════════════════════════════════════

interface Step3Props {
  meetingDetails: MeetingDetails;
  attendees: ExtraordinaryMeetingAttendee[];
  decisions: ExtraordinaryMeetingDecisions;
  prefillData: ExtraordinaryMeetingPrefillData | null;
}

function Step3Review({ meetingDetails, attendees, decisions, prefillData }: Step3Props) {
  const presentAttendees = attendees.filter((a) => a.present);
  const totalShares = attendees.reduce((sum, a) => sum + a.shares, 0);
  const presentShares = presentAttendees.reduce((sum, a) => sum + a.shares, 0);

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-600">
        Granska uppgifterna innan du skapar protokollet.
      </p>

      {/* Header */}
      <div className="p-4 bg-primary-50 rounded-lg border border-primary-200 text-center">
        <p className="text-lg font-bold text-primary-700">
          Extra bolagsstämma
        </p>
        <p className="text-sm text-primary-600">{prefillData?.company.name}</p>
        <p className="text-xs text-primary-500 mt-1">
          {REASON_LABELS[decisions.reason]}
        </p>
      </div>

      {/* Meeting Info */}
      <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
        <h4 className="font-medium text-gray-900 mb-3">Mötesuppgifter</h4>
        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-gray-600">Datum:</span>
            <span className="font-medium">{meetingDetails.meetingDate}</span>
          </div>
          {meetingDetails.meetingTime && (
            <div className="flex justify-between">
              <span className="text-gray-600">Tid:</span>
              <span className="font-medium">{meetingDetails.meetingTime}</span>
            </div>
          )}
          {meetingDetails.meetingPlace && (
            <div className="flex justify-between">
              <span className="text-gray-600">Plats:</span>
              <span className="font-medium">{meetingDetails.meetingPlace}</span>
            </div>
          )}
          <div className="flex justify-between">
            <span className="text-gray-600">Ordförande:</span>
            <span className="font-medium">{meetingDetails.chairpersonName}</span>
          </div>
        </div>
      </div>

      {/* Attendees */}
      <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
        <h4 className="font-medium text-gray-900 mb-3">
          Närvarande ({presentAttendees.length} aktieägare, {presentShares.toLocaleString("sv-SE")} aktier)
        </h4>
        <div className="space-y-1 text-sm">
          {presentAttendees.map((a, i) => (
            <p key={i} className="text-gray-700">
              {a.name}
              <span className="text-gray-500">
                {" "}({a.shares.toLocaleString("sv-SE")} aktier)
              </span>
              {a.representedBy && (
                <span className="text-gray-400 italic"> genom {a.representedBy}</span>
              )}
            </p>
          ))}
        </div>
        <p className="mt-2 text-xs text-gray-500">
          Representerat: {Math.round((presentShares / totalShares) * 100)}% av samtliga aktier
        </p>
      </div>

      {/* Decisions Summary */}
      <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
        <h4 className="font-medium text-gray-900 mb-3">Ärende: {REASON_LABELS[decisions.reason]}</h4>
        <div className="text-sm text-gray-700">
          {decisions.reason === "capital_change" && decisions.capitalChange && (
            <div>
              <p>
                {decisions.capitalChange.type === "increase" ? "Ökning" : "Minskning"} av
                aktiekapitalet med {decisions.capitalChange.amount.toLocaleString("sv-SE")} kr
              </p>
              {decisions.capitalChange.method && (
                <p className="text-gray-500">Metod: {decisions.capitalChange.method}</p>
              )}
            </div>
          )}

          {decisions.reason === "bylaw_amendment" && decisions.bylawAmendment && (
            <div>
              {decisions.bylawAmendment.sections.length > 0 && (
                <p className="text-gray-500">
                  Ändrade paragrafer: {decisions.bylawAmendment.sections.join(", ")}
                </p>
              )}
              <p>{decisions.bylawAmendment.description}</p>
            </div>
          )}

          {decisions.reason === "board_change" && decisions.boardElection && (
            <div>
              <p className="mb-2">Följande styrelseledamöter väljs:</p>
              <ul className="list-disc list-inside space-y-1">
                {decisions.boardElection.members.map((m, i) => (
                  <li key={i}>
                    {m.name} <span className="text-gray-500">({m.role})</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {decisions.reason === "minority_request" && decisions.reasonDescription && (
            <p>{decisions.reasonDescription}</p>
          )}

          {decisions.reason === "other" && (
            <div>
              {decisions.reasonDescription && <p className="mb-2">{decisions.reasonDescription}</p>}
              {decisions.otherDecisions && decisions.otherDecisions.length > 0 && (
                <div className="space-y-2 mt-2">
                  {decisions.otherDecisions.map((d, i) => (
                    <div key={i} className="pl-2 border-l-2 border-gray-300">
                      <p className="font-medium">§ {i + 1}: {d.title}</p>
                      <p className="text-gray-500">{d.decision}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="p-3 bg-green-50 rounded-lg border border-green-200">
        <p className="text-sm text-green-800">
          <AlertCircle className="w-4 h-4 inline mr-1" />
          Efter att protokollet skapats kan du ladda ner det som PDF och redigera det tills det slutförts.
        </p>
      </div>
    </div>
  );
}

export default ExtraordinaryMeetingWizard;
